import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { analytics } from 'analytics';
import { useGlobalModalsContext } from 'contexts/globalModalsContext';
import Avatar from 'ui/Avatar';
import Button from 'ui/Button';
import Icon from 'ui/Icon';
import { Flex } from 'ui/Layout';
import Link from 'ui/Link';
import Menu from 'ui/Menu';
import { Text } from 'ui/Typography';

import { useApiContext } from '../../../contexts/apiContext';
import { useAppEnvironmentContext } from '../../../contexts/appEnvironmentContext';
import { Path, websiteLinkTo } from '../../../utils/routes';
import { useCurrentUser } from '../../../utils/user';
import UserMenuLink from './UserMenuLink';

const UserSection: React.FC = () => {
  const { openSignUpModal, openSignInModal } = useGlobalModalsContext();

  const { user, refetchUser } = useCurrentUser();
  const { appEnvironment } = useAppEnvironmentContext();
  const { api } = useApiContext();
  const navigate = useNavigate();

  const signOutMutation = useMutation(async () => {
    try {
      await api().currentUser.signOut();
      await refetchUser();
      if (appEnvironment === 'web') {
        window.location.href = websiteLinkTo('/');
      } else {
        navigate(Path.AddinHome);
      }
    } catch (error) {
      analytics.reportError('Error signing out', { error });
    }
  });

  const handleSignOut = useCallback(() => {
    if (appEnvironment === 'web') {
      window.location.href = websiteLinkTo('/accounts/logout');
    } else {
      signOutMutation.mutate();
    }
  }, [appEnvironment]);

  return user ? (
    <Menu.Root placement="bottom-end">
      <Menu.Trigger>
        <Link variant="neutral">
          <Avatar src={user.avatar_url} size="sm" />
          <Text display={{ base: 'none', md: 'block' }}>My account</Text>
          <Icon
            display={{ base: 'none', md: 'block' }}
            icon="caret-down"
            fontSize="xs"
          />
        </Link>
      </Menu.Trigger>
      <Menu.Content>
        <UserMenuLink
          icon="user"
          value="profile"
          label="Profile"
          href={websiteLinkTo('/users/profile')}
        />
        <UserMenuLink
          icon="user-group"
          value="organization"
          label="Organization"
          href={websiteLinkTo('/teams/manage')}
        />
        <UserMenuLink
          icon="crown"
          value="subscription"
          label="Subscription"
          href={websiteLinkTo('/subscriptions')}
        />
        <UserMenuLink
          icon="comment"
          value="support"
          label="Support"
          href={websiteLinkTo('/users/support')}
        />
        <Menu.Separator />
        <UserMenuLink
          icon="arrow-right-from-bracket"
          value="sign-out"
          label="Sign out"
          onClick={handleSignOut}
        />
        {user.is_staff && (
          <>
            <Menu.Separator />
            <UserMenuLink
              icon="palette"
              value="analytics"
              label="Analytics"
              href={websiteLinkTo('/internal/analytics')}
              isDanger
            />
            <UserMenuLink
              icon="list-ol"
              value="user-list"
              label="User list"
              href={websiteLinkTo('/internal/list-of-users')}
              isDanger
            />
            {user.is_superuser && (
              <UserMenuLink
                icon="key"
                value="django-admin"
                label="Django admin"
                href={websiteLinkTo('/admin')}
                isDanger
              />
            )}
            <UserMenuLink
              icon="blog"
              value="blog-cms"
              label="Blog CMS"
              href={websiteLinkTo('/cms')}
              isDanger
            />
          </>
        )}
      </Menu.Content>
    </Menu.Root>
  ) : (
    <Flex alignItems="center" gap={4}>
      <Link display={{ base: 'none', md: 'block' }} onClick={openSignInModal}>
        Sign in
      </Link>
      <Button variant="primary-outline" onClick={openSignUpModal}>
        Sign up
      </Button>
    </Flex>
  );
};

export default UserSection;
